import {
  updateBeaconAssociation,
  associateBeaconToLocation,
  deleteBeaconAssociation,
} from '../services/apiServices';
import { feedbackMessages } from '../constants';
import { useFeedbackContext } from '../context/FeedbackContext';

/**
 * Custom hook that handles beacon location actions (edit, add, and delete).
 *
 * This hook:
 *  - Invokes API functions (update, add, or delete) for beacon associations.
 *  - Uses `setIsLoading` to reflect global loading state during operations.
 *  - Refreshes beacon data via `fetchBeacons` after each operation.
 *  - Clears or updates selected rows using `setSelectedRows` as needed.
 *  - Displays success or error feedback modals using the global FeedbackContext.
 *
 * @param {Function} setIsLoading - A function to set the global loading state.
 * @param {Function} fetchBeacons - A function that re-fetches beacon data.
 * @param {Function} setSelectedRows - A function to clear/update selected rows after delete operations.
 * @returns {Object} - An object containing methods for beacon actions (edit, add, delete).
 */
const useBeaconLocationActions = (setIsLoading, fetchBeacons, setSelectedRows) => {
  const { showModal, hideModal } = useFeedbackContext();

  const showFeedbackModal = (feedbackMessage) => {
    showModal({
      variant: feedbackMessage.variant,
      title: feedbackMessage.title,
      copy: feedbackMessage.copy,
      buttonMessage: feedbackMessage.buttonMessage,
      onClickButton: () => {
        hideModal();
      },
    });
  };

  const handleEditBeacon = async (id, params) => {
    setIsLoading(true);
    try {
      await updateBeaconAssociation(id, params);
      showFeedbackModal(feedbackMessages.SUCCESS_BEACON_EDITED);
    } catch (error) {
      showFeedbackModal(feedbackMessages.ERROR_BEACON_EDIT);
    } finally {
      setIsLoading(false);
      fetchBeacons();
    }
  };

  const handleAddBeacon = async (params) => {
    setIsLoading(true);
    try {
      await associateBeaconToLocation(params);
      showFeedbackModal(feedbackMessages.SUCCESS_BEACON_ADDED);
    } catch (error) {
      showFeedbackModal(feedbackMessages.ERROR_BEACON_ADD);
    } finally {
      setIsLoading(false);
      fetchBeacons();
    }
  };

  const handleDelete = async (ids) => {
    setIsLoading(true);
    try {
      await Promise.all(ids.map((id) => deleteBeaconAssociation(id)));
      showFeedbackModal(feedbackMessages.SUCCESS_BEACON_DELETE);
    } catch (error) {
      showFeedbackModal(feedbackMessages.ERROR_DELETE_ASSOCIATION);
    } finally {
      setIsLoading(false);
      fetchBeacons();
      setSelectedRows([]);
    }
  };

  return {
    handleEditBeacon,
    handleAddBeacon,
    handleDelete,
  };
};

export default useBeaconLocationActions;
